/**
 * The app's one filter-and-sort pattern for tables (Pedro U3, 2026-10-29: "P2" — a menu on each
 * column heading: sort Asc / Desc and a filter typed to the column, drafted until OK; active
 * headings blue; C-293). The column menu itself is `GridColumnMenu`; this hook adds the shared
 * state, the filtering and sorting of the rows, the heading cells and the "Filtered: n of m"
 * line, so a table only declares its columns. Client-side: for tables whose rows are all loaded
 * (a server-paged list keeps filtering in the database — never load all its rows to use this).
 * Used by Portfolios → Assign to… (both tabs); Trades / Securities / View portfolio predate it.
 */
import { useMemo, useState } from 'react';
import { Anchor, Badge, Group, Popover, Table } from '@mantine/core';
import { IconFilter, IconSortAscending, IconSortDescending } from '@tabler/icons-react';

import { ColumnMenu, filterActive, passesFilter, type Filters, type Ft, type SortState } from './GridColumnMenu';

export interface GridCol<T> {
  key: string;
  label: string;
  ft: Ft;
  width?: number;
  /** the text the column filters and sorts on (for enums, the value listed in the menu) */
  text: (row: T) => string;
  /** a number to sort and range-filter on, when the column is numeric */
  num?: (row: T) => number | null;
}

const TH = { whiteSpace: 'nowrap' as const, verticalAlign: 'bottom' as const };

export function useGridHeaders<T>(rows: T[], cols: GridCol<T>[], noun: string) {
  const [filters, setFilters] = useState<Filters>({});
  const [sort, setSort] = useState<SortState | null>(null);
  const [openCol, setOpenCol] = useState<string | null>(null);

  const options = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const c of cols) if (c.ft === 'enum') out[c.key] = [...new Set(rows.map((r) => c.text(r)))].sort();
    return out;
  }, [cols, rows]);

  const shown = useMemo(() => {
    const active = cols.filter((c) => filterActive(c, filters[c.key]));
    const out = rows.filter((r) => active.every((c) => passesFilter(c.ft, filters[c.key], c.text(r), c.num ? c.num(r) : null)));
    const sc = sort && cols.find((c) => c.key === sort.key);
    if (sc) {
      const v = (r: T): number | string => (sc.num ? (sc.num(r) ?? -Infinity) : sc.text(r));
      out.sort((a, b) => {
        const x = v(a), y = v(b);
        const d = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
        return sort.dir === 'desc' ? -d : d;
      });
    }
    return out;
  }, [rows, cols, filters, sort]);

  const head = (c: GridCol<T>): React.ReactNode => {
    const active = filterActive(c, filters[c.key]) || sort?.key === c.key;
    return (
      <Table.Th key={c.key} style={{ ...TH, width: c.width, background: active ? 'var(--mantine-color-azul-1)' : undefined }}>
        <Popover width={250} position="bottom-start" withArrow shadow="md" trapFocus keepMounted={false}
          opened={openCol === c.key} onChange={(o) => setOpenCol(o ? c.key : null)}>
          <Popover.Target>
            <Group gap={4} wrap="nowrap" role="button" aria-label={`Sort or filter ${c.label}`} style={{ cursor: 'pointer' }}
              onClick={() => setOpenCol(openCol === c.key ? null : c.key)}>
              <span>{c.label}</span>
              <span style={{ color: active ? 'var(--mantine-color-azul-7)' : 'var(--mantine-color-gray-5)', display: 'inline-flex' }}>
                {sort?.key === c.key ? (sort.dir === 'asc' ? <IconSortAscending size={13} /> : <IconSortDescending size={13} />) : <IconFilter size={13} />}
              </span>
            </Group>
          </Popover.Target>
          <Popover.Dropdown>
            <ColumnMenu col={c} options={options[c.key] ?? []} initialFilter={filters[c.key]} sort={sort}
              onSort={(dir) => { setSort((p) => (p && p.key === c.key && p.dir === dir ? null : { key: c.key, dir })); setOpenCol(null); }}
              onApply={(draft) => { setFilters((p) => ({ ...p, [c.key]: draft })); setOpenCol(null); }}
              onClear={() => { setFilters((p) => { const n = { ...p }; delete n[c.key]; return n; }); setSort((p) => (p?.key === c.key ? null : p)); setOpenCol(null); }} />
          </Popover.Dropdown>
        </Popover>
      </Table.Th>
    );
  };

  const nFiltered = cols.filter((c) => filterActive(c, filters[c.key])).length;
  const summary = (nFiltered > 0 || sort) ? (
    <Group gap="xs">
      {nFiltered > 0 && <Badge color="azul" variant="light" style={{ textTransform: 'none' }}>Filtered: {shown.length} of {rows.length} {noun}</Badge>}
      <Anchor size="xs" onClick={() => { setFilters({}); setSort(null); }}>Clear filters and sort</Anchor>
    </Group>
  ) : null;

  return { shown, head, summary };
}
